import React, { useEffect, useState } from "react";
import { Button as RsuiteButton, ButtonProps } from "rsuite";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import {
    IWorkflowStep,
    IWorkflowWithData,
    StudioWorkflow,
    WorkflowStatus,
} from "store/workflows/workflows-types";

import { hasFailedSteps } from "components/Workflows/workflows-helper";

import StudioProgress from "components/Workflows/Studio/Progress";

import "./Button.less";

interface Props extends Omit<ButtonProps, "onClick" | "appearance"> {
    environmentId: string;
    tenantId: string;

    workflow: IWorkflowWithData<IWorkflowStep, any> | null;
    onTerminated?: () => void;
    onResolved?: () => void;
}

const Button: React.FC<Props> = ({
    environmentId,
    tenantId,
    workflow,
    className,
    onTerminated,
    onResolved,
    ...props
}: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (workflow?.status === WorkflowStatus.Succeeded) {
            setOpen(false);
        }
    }, [workflow]);

    if (!workflow) {
        return <></>;
    }

    const stepFailed = hasFailedSteps(workflow.steps);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setOpen(true);
    };

    const getText = (): string => {
        switch (workflow.status) {
            case WorkflowStatus.Terminated:
                return t("workflow.button.canceled");
            case WorkflowStatus.Succeeded:
            case WorkflowStatus.Failed:
                return t("workflow.button.history");
            default:
                return t("workflow.button.progress");
        }
    };

    const getColor = (): string => {
        switch (workflow.status) {
            case WorkflowStatus.Terminated:
                return "workflow-button--terminated";
            case WorkflowStatus.Failed:
                return "workflow-button--failed";
            case WorkflowStatus.InProgress:
                if (stepFailed) {
                    return "workflow-button--failed";
                }

                return "workflow-button--normal";
            default:
                return "workflow-button--normal";
        }
    };

    const getProgress = () => {
        const handleClose = () => setOpen(false);

        if (workflow as StudioWorkflow) {
            return (
                <StudioProgress
                    open={open}
                    environmentId={environmentId}
                    tenantId={tenantId}
                    workflow={workflow}
                    onClose={handleClose}
                    onTerminated={onTerminated}
                    onResolved={onResolved}
                />
            );
        } else return <></>;
    };

    const text = getText();
    const color = getColor();

    const progress = getProgress();

    return (
        <>
            {progress}
            <RsuiteButton
                className={cn("workflow-button", color, className)}
                appearance="link"
                onClick={handleClick}
                {...props}
            >
                {text}
            </RsuiteButton>
        </>
    );
};

export default Button;
